import { Grow } from '@mui/material';
import _ from 'lodash';
import React, { useContext } from 'react';
import { MessagingContext } from '../../../services';
import { formatDate } from '../../../services/primitives/date-utils';
import { Tooltip } from '../../base';
import MarkdownWrapper from '../../base/markdown-wrapper/markdown-wrapper';
import { MessagingBadge, MessagingPill } from '../messaging-badge';
import { UserAvatarLogo, UserAvatarLogoContainer } from '../message-list.styles';
import enterLogo from '../icons/enter-services-icon.svg';
import { authorNickname, IMessagingComment, isEnterAuthor, isHtmlContent, sanitizeHtml } from '../util';
import { getEarliestComment } from './comment-reason.utils';
import { isResolveMarkerComment } from './resolve-section';
import * as S from './chat-history.styles';

const getInitials = (name: string): string => {
    if (!name) return '';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
};

interface IChatHistory {
    hideNote?: boolean;
}

export const ChatHistory: React.FunctionComponent<IChatHistory> = (props: IChatHistory): React.ReactElement => {
    const { hideNote } = props;
    const context = useContext(MessagingContext);
    const { comments, documents, account } = context;

    const parseDate = (d: string | undefined): Date => (d ? new Date(d) : new Date());

    const isOwnComment = (comment: IMessagingComment): boolean => {
        const authorId = comment.author?.id;
        if (!authorId || !account) return false;
        return authorId === account.personalAccountId || authorId === account.userId;
    };

    // NB: skip first comment, as it is shown in the chat body
    const renderAttachments = (comment: IMessagingComment): React.ReactElement | React.ReactElement[] | undefined => {
        const commentDocIds = comment.documents || [];
        const commentDocs = _.filter(documents, document => _.includes(commentDocIds, document.id));

        // Only while the detail is ACTUALLY loading. Without that condition this is a spinner
        // with no end state: when the documents never arrive the comment sits on "Loading
        // documents..." for as long as the panel is open, which is precisely the failure NS-14
        // names — a spinner is indistinguishable from a screen that has quietly stopped working.
        //
        // It is reachable: opening a task by CLICKING it in the list populates the panel from the
        // row and skips the detail load (`messaging-redesigned.tsx` "justPopulated"), so comments
        // render with document ids while `documents` is still undefined. Loading the same URL
        // directly does fetch, and resolves — so the same task looked broken or fine depending on
        // how it was opened.
        if (commentDocIds.length > 0 && commentDocs.length === 0) {
            return context.isDetailLoading ? <S.LoadingText>Loading documents...</S.LoadingText> : undefined;
        }

        return _.times(_.size(commentDocs), (i: number): React.ReactElement => {
            const doc = commentDocs[i];
            const onDownload = async (): Promise<void> => {
                if (doc.id) {
                    await (context.onDocumentDownload && context.onDocumentDownload(doc.id));
                }
            };

            const onActualDownload = async (): Promise<string | void> => {
                if (doc && doc.id && context.onDocumentActualDownload) {
                    return await context.onDocumentActualDownload(doc.id);
                }
            };

            const onAssign = async (): Promise<void> => {
                await (context.onDocumentAssign && context.onDocumentAssign(doc));
            };

            const onEdit = async (): Promise<void> => {
                await (context.onDocumentEdit && context.onDocumentEdit(doc));
            };

            const isSupportApp = context.account?.supportGrant === true;
            const canAssign =
                context.message?.links?.claims?.length &&
                context.message?.links?.claims?.length > 0 &&
                !doc.claimId &&
                !!context.onDocumentAssign;

            return (
                <S.HistoryEntryAttachment key={`attachment-${i}`}>
                    <S.ConversationAttachment
                        key={`attachment-${i}`}
                        filename={doc.filename as string}
                        filesize={doc.filesize}
                        thumbnail={doc.thumbnail}
                        documentId={doc.id}
                        onDownload={onDownload}
                        onActualDownload={
                            context.onDocumentActualDownload ? onActualDownload : undefined
                        }
                        onAssign={canAssign ? onAssign : undefined}
                        onEdit={context.onDocumentEdit ? onEdit : undefined}
                        isSupportApp={isSupportApp}
                    />
                </S.HistoryEntryAttachment>
            );
        });
    };

    const renderComment = (comment: IMessagingComment): React.ReactElement => {
        const hasNote = _.get(comment, 'note', false);
        const isHtml = isHtmlContent(comment.body);
        const isOwn = isOwnComment(comment);
        const authorName = isOwn ? 'You' : authorNickname(comment);
        const authorInitials = getInitials(authorNickname(comment));
        const commentDate = parseDate(comment.created);
        const timestamp = formatDate(commentDate, 'MMM d, h:mm a');
        const isEnter = isEnterAuthor(comment);

        const authorAvatar =
            isOwn ? null : isEnter ? (
                <Tooltip direction="top" title={<span>{authorNickname(comment)}</span>}>
                    <UserAvatarLogoContainer>
                        <UserAvatarLogo src={enterLogo} alt={authorNickname(comment)} />
                    </UserAvatarLogoContainer>
                </Tooltip>
            ) : (
                <MessagingBadge title={authorNickname(comment)}>{authorInitials || '?'}</MessagingBadge>
            );

        return (
            <Grow in={true} key={comment.id}>
                <S.HistoryEntry note={hasNote} own={isOwn}>
                    {authorAvatar}
                    <S.HistoryEntryContent own={isOwn}>
                        <S.HistoryEntryHeader>
                            <S.HistoryEntryName>{authorName}</S.HistoryEntryName>
                            {isEnter && <MessagingPill>AI Message</MessagingPill>}
                            <S.HistoryEntryTime>{timestamp}</S.HistoryEntryTime>
                            {hasNote && (
                                <S.HistoryNoteIndicator>
                                    <MessagingPill note={true}>Internal Note</MessagingPill>
                                </S.HistoryNoteIndicator>
                            )}
                        </S.HistoryEntryHeader>
                        <S.HistoryEntryMessage note={hasNote} own={isOwn}>
                            {isHtml ? (
                                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(comment.body) }} />
                            ) : (
                                <MarkdownWrapper text={comment.body || ''} />
                            )}
                        </S.HistoryEntryMessage>
                        {renderAttachments(comment)}
                    </S.HistoryEntryContent>
                </S.HistoryEntry>
            </Grow>
        );
    };

    const renderComments = (): React.ReactElement[] => {
        // Skip the chronologically first comment — it is shown in ChatBody.
        const earliest = getEarliestComment(comments as IMessagingComment[] | undefined);
        const historyComments = _.filter(
            comments as IMessagingComment[],
            c => !!c && c.id !== earliest?.id,
        );
        return _.compact(
            _.map(historyComments, (comment: IMessagingComment | undefined): React.ReactElement | null => {
                if (!comment) return null;
                if (hideNote && comment.note) return null;
                // Resolve action replies are represented by the Success card, not Conversation.
                if (isResolveMarkerComment(comment.body)) return null;
                return renderComment(comment);
            })
        );
    };

    const renderedComments = renderComments();
    if (renderedComments.length === 0) {
        return <></>;
    }

    return (
        <S.History>
            <S.CommentsHeader>Conversation</S.CommentsHeader>
            <S.HistoryBody>{renderedComments}</S.HistoryBody>
        </S.History>
    );
};
